import { TransactionTypes as GeneratedTransactionTypes } from '@generated/graphql';
import { dollars2cents } from '@utils/dollars2cents';
import type { Account } from './account';
import type { FinancialProduct } from './financialProduct';
import type { Goal, SubAccount } from './goalSubAccountTypes';
import type { User } from './user';

export enum TransactionTypes {
  EFT = 'EFT',
  CANCEL_EFT = 'CANCEL_EFT',
  BUY = 'BUY',
  CANCEL_BUY = 'CANCEL_BUY',
  SELL = 'SELL',
  CANCEL_SELL = 'CANCEL_SELL',
  DIVIDEND = 'DIVIDEND',
  EXTERNAL_TRANSFER_IN = 'EXTERNAL_TRANSFER_IN',
  EXTERNAL_TRANSFER_OUT = 'EXTERNAL_TRANSFER_OUT',
  TRANSFER_IN = 'TRANSFER_IN',
  TRANSFER_OUT = 'TRANSFER_OUT',
  CANCEL_TRANSFER = 'CANCEL_TRANSFER',
  CORPORATE_ACTION = 'CORPORATE_ACTION',
  CANCEL_CORPORATE_ACTION = 'CANCEL_CORPORATE_ACTION',
  MANAGEMENT_FEE = 'MANAGEMENT_FEE',
  CANCEL_MANAGEMENT_FEE = 'CANCEL_MANAGEMENT_FEE',
  BORROW_FEE = 'BORROW_FEE',
  MISC_FEE = 'MISC_FEE',
  INTEREST = 'INTEREST',
  RISK_EXPOSURE_FEE = 'RISK_EXPOSURE_FEE',
  WITHHOLDING_TAX = 'WITHHOLDING_TAX',
  DEREGISTRATION_FEE = 'DEREGISTRATION_FEE',
  DEREGISTRATION_TAX = 'DEREGISTRATION_TAX',
  GST = 'GST',
  HST = 'HST',
  ADJUSTMENT = 'ADJUSTMENT',
  GOV_CONTRIBUTIONS = 'GOV_CONTRIBUTIONS',
  SWITCH_IN = 'SWITCH_IN',
  CANCEL_SWITCH_IN = 'CANCEL_SWITCH_IN',
  CANCEL_SWITCH_OUT = 'CANCEL_SWITCH_OUT',
  DISTRIBUTION = 'DISTRIBUTION',
  CHEQUE = 'CHEQUE',
  DEPOSIT_BUY = 'DEPOSIT_BUY',
  DISTRIBUTION_REINVESTMENT = 'DISTRIBUTION_REINVESTMENT',
  EXTERNAL_TRANSFER_IN_SECURITY = 'EXTERNAL_TRANSFER_IN_SECURITY',
  EXPENSE = 'EXPENSE',
  OTHER = 'OTHER',
  TRANSFER_IN_SECURITY = 'TRANSFER_IN_SECURITY',
  TRANSFER_OUT_SECURITY = 'TRANSFER_OUT_SECURITY',
  SWITCH_OUT = 'SWITCH_OUT', // TODO: deprecated
  SWI_MARKET = 'SWI_MARKET',
  SWI_COST = 'SWI_COST',
  SWO_MARKET = 'SWO_MARKET',
  SWO_COST = 'SWO_COST',
  MARKET_DRIP = 'MARKET_DRIP',
  ACCRUED_INTEREST = 'ACCRUED_INTEREST',
  CANADA_LEARNING_BOND = 'CANADA_LEARNING_BOND',
  COMMISSION = 'COMMISSION',
  DISABILITY_ASSISTANCE_PAYMENT = 'DISABILITY_ASSISTANCE_PAYMENT',
  ADD_CAD_EDU_SAVING_GRANT = 'ADD_CAD_EDU_SAVING_GRANT',
  HOME_BUYER_PLAN = 'HOME_BUYER_PLAN',
  LIFE_LEARNING_PLAN = 'LIFE_LEARNING_PLAN',
  MANAGEMENT_FEE_REDEMPTION = 'MANAGEMENT_FEE_REDEMPTION',
  REBATE = 'REBATE',
  REDEMPTION = 'REDEMPTION',
  RETURN_OF_CAPITAL = 'RETURN_OF_CAPITAL',
  WIRE_TRANSFER = 'WIRE_TRANSFER',
  WRITE_OFF = 'WRITE_OFF',
  STOCK_DRIP = 'STOCK_DRIP',
  CANCEL_BORROW_FEE = 'CANCEL_BORROW_FEE',
  CANCEL_CHEQUE = 'CANCEL_CHEQUE',
  CANCEL_DEPOSIT = 'CANCEL_DEPOSIT',
  CANCEL_FEE = 'CANCEL_FEE',
  CANCEL_HST = 'CANCEL_HST',
  CANCEL_INTEREST = 'CANCEL_INTEREST',
  CANCEL_REBATE = 'CANCEL_REBATE',
  CANCEL_TRANSFER_IN = 'CANCEL_TRANSFER_IN',
  CANCEL_TRANSFER_OUT = 'CANCEL_TRANSFER_OUT',
  FEDERAL_TAX = 'FEDERAL_TAX',
  SHORT_TERM_GAIN_LOSS = 'SHORT_TERM_GAIN_LOSS',
  LONG_TERM_GAIN_LOSS = 'LONG_TERM_GAIN_LOSS',
  NON_EDU_CAPITAL_WITHDRAWAL = 'NON_EDU_CAPITAL_WITHDRAWAL',
  CANCEL_ADJUSTMENT = 'CANCEL_ADJUSTMENT',
  CONTRIBUTION = 'CONTRIBUTION',
  BOOK_VALUE_ADJUSTMENT = 'BOOK_VALUE_ADJUSTMENT',
  JOURNAL = 'JOURNAL',
  CANCEL_JOURNAL = 'CANCEL_JOURNAL',
  FOREIGN_TAX = 'FOREIGN_TAX',
  TRANSFERS_EXCHANGES = 'TRANSFERS_EXCHANGES',
}

export enum JournalTypes {
  CASH = 'CASH',
  SECURITY = 'SECURITY',
  BOTH = 'BOTH',
}

export enum SwitchTransactionTypes {
  SWO_COST = 'SWO_COST',
  SWI_COST = 'SWI_COST',
  SWO_MARKET = 'SWO_MARKET',
  SWI_MARKET = 'SWI_MARKET',
}

export enum SwitchTypes {
  AT_COST = 'AT_COST',
  AT_MARKET = 'AT_MARKET',
  SWO_COST = 'SWO_COST',
  CUSTODY_FEE = 'CUSTODY_FEE',
  INCOME_FUND_TRANSFER = 'INCOME_FUND_TRANSFER',
}

export enum TransferSourcesTypes {
  USER = 'USER',
  REPRESENTATIVE = 'REPRESENTATIVE',
  FEE = 'FEE',
  DEREGISTRATION = 'DEREGISTRATION',
  DEREGISTRATION_TAX = 'DEREGISTRATION_TAX',
  WITHHOLDING_TAX = 'WITHHOLDING_TAX',
  DIVIDEND = 'DIVIDEND',
  TO_SUB_ACCOUNT = 'TO_SUB_ACCOUNT',
  FROM_SUB_ACCOUNT = 'FROM_SUB_ACCOUNT',
  TO_ACCOUNT = 'TO_ACCOUNT',
  FROM_ACCOUNT = 'FROM_ACCOUNT',
  EXTERNAL_TRANSFER_IN = 'EXTERNAL_TRANSFER_IN',
  EXTERNAL_TRANSFER_OUT = 'EXTERNAL_TRANSFER_OUT',
  ADJUSTMENT = 'ADJUSTMENT',
  CUSTODIAN_ACTIVITY = 'CUSTODIAN_ACTIVITY',
  CHEQUE = 'CHEQUE',
  GOV_CONTRIBUTIONS = 'GOV_CONTRIBUTIONS',
  DISTRIBUTION = 'DISTRIBUTION',
}

export enum Currencies {
  CAD = 'CAD',
  USD = 'USD',
}

export enum TransactionObjectTypes {
  TRANSFER = 'Transfer',
  SUB_TRADE = 'SubTrade',
  CUSTODIAN_ACTIVITY = 'CustodianActivity',
  ADJUSTMENT = 'Adjustment',
  JOURNAL = 'Journal',
}

export enum InterestTypes {
  INTEREST_IN = 'INTEREST_IN',
  INTEREST_OUT = 'INTEREST_OUT',
}

export const TRADE_TYPES = [
  TransactionTypes.BUY,
  TransactionTypes.SELL,
  TransactionTypes.CANCEL_BUY,
  TransactionTypes.CANCEL_SELL,
  TransactionTypes.SWI_MARKET,
  TransactionTypes.SWI_COST,
  TransactionTypes.SWITCH_IN,
  TransactionTypes.CANCEL_SWITCH_IN,
  TransactionTypes.SWO_MARKET,
  TransactionTypes.SWO_COST,
  TransactionTypes.SWITCH_OUT,
  TransactionTypes.CANCEL_SWITCH_OUT,
  TransactionTypes.DEPOSIT_BUY,
  TransactionTypes.DISTRIBUTION_REINVESTMENT,
  TransactionTypes.DIVIDEND,
  TransactionTypes.EXTERNAL_TRANSFER_IN_SECURITY,
  TransactionTypes.TRANSFER_IN_SECURITY,
  TransactionTypes.TRANSFER_OUT_SECURITY,
  TransactionTypes.CORPORATE_ACTION,
  TransactionTypes.CANCEL_CORPORATE_ACTION,
  TransactionTypes.MARKET_DRIP,
  TransactionTypes.STOCK_DRIP,
  TransactionTypes.CANCEL_TRANSFER_IN,
  TransactionTypes.CANCEL_TRANSFER_OUT,
];

const generatedTransactionTypesMap = Object.entries(TransactionTypes).reduce(
  (map, [, v]) => {
    // Convert SNAKE_CASE to PascalCase (e.g., 'ACCRUED_INTEREST' -> 'AccruedInterest')
    const pascalCaseKey = v
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join('');
    map.set(
      v,
      GeneratedTransactionTypes[
        pascalCaseKey as keyof typeof GeneratedTransactionTypes
      ],
    );
    return map;
  },
  new Map<TransactionTypes, GeneratedTransactionTypes>(),
);

const mapToGeneratedTypes = (
  types: TransactionTypes[],
): GeneratedTransactionTypes[] => {
  return types.map((type) => {
    const generatedType = generatedTransactionTypesMap.get(type);
    if (!generatedType) {
      throw new Error(`No generated transaction type found for: ${type}`);
    }
    return generatedType;
  });
};

export const GENERATED_TRADE_TYPES = mapToGeneratedTypes(TRADE_TYPES);

export const POSITIVE_VALUE_TYPES = [
  TransactionTypes.SELL,
  TransactionTypes.CANCEL_BUY,
  TransactionTypes.EXTERNAL_TRANSFER_IN,
  TransactionTypes.TRANSFER_IN,
  TransactionTypes.DIVIDEND,
  TransactionTypes.CANCEL_SWITCH_IN,
  TransactionTypes.DISTRIBUTION,
  TransactionTypes.CANADA_LEARNING_BOND,
  TransactionTypes.COMMISSION,
  TransactionTypes.DISABILITY_ASSISTANCE_PAYMENT,
  TransactionTypes.ADD_CAD_EDU_SAVING_GRANT,
  TransactionTypes.MANAGEMENT_FEE_REDEMPTION,
  TransactionTypes.REBATE,
  TransactionTypes.REDEMPTION,
  TransactionTypes.RETURN_OF_CAPITAL,
  TransactionTypes.WIRE_TRANSFER,
  TransactionTypes.CANCEL_BORROW_FEE,
  TransactionTypes.CANCEL_FEE,
  TransactionTypes.CANCEL_HST,
  TransactionTypes.CANCEL_TRANSFER_IN,
  TransactionTypes.TRANSFER_OUT_SECURITY,
  TransactionTypes.SWI_MARKET,
  TransactionTypes.SWI_COST,
];
export const GENERATED_POSITIVE_VALUE_TYPES =
  mapToGeneratedTypes(POSITIVE_VALUE_TYPES);

export const NEGATIVE_VALUE_TYPES = [
  TransactionTypes.BUY,
  TransactionTypes.CANCEL_SELL,
  TransactionTypes.EXTERNAL_TRANSFER_OUT,
  TransactionTypes.TRANSFER_OUT,
  TransactionTypes.BORROW_FEE,
  TransactionTypes.RISK_EXPOSURE_FEE,
  TransactionTypes.WITHHOLDING_TAX,
  TransactionTypes.DEREGISTRATION_FEE,
  TransactionTypes.DEREGISTRATION_TAX,
  TransactionTypes.GST,
  TransactionTypes.HST,
  TransactionTypes.CANCEL_SWITCH_OUT,
  TransactionTypes.DEPOSIT_BUY,
  TransactionTypes.EXTERNAL_TRANSFER_IN_SECURITY,
  TransactionTypes.DISTRIBUTION_REINVESTMENT,
  TransactionTypes.TRANSFER_IN_SECURITY,
  TransactionTypes.HOME_BUYER_PLAN,
  TransactionTypes.LIFE_LEARNING_PLAN,
  TransactionTypes.STOCK_DRIP,
  TransactionTypes.MARKET_DRIP,
  TransactionTypes.FEDERAL_TAX,
  TransactionTypes.CANCEL_DEPOSIT,
  TransactionTypes.NON_EDU_CAPITAL_WITHDRAWAL,
  TransactionTypes.CANCEL_REBATE,
  TransactionTypes.CANCEL_TRANSFER_OUT,
  TransactionTypes.SWO_MARKET,
  TransactionTypes.SWO_COST,
];
export const GENERATED_NEGATIVE_VALUE_TYPES =
  mapToGeneratedTypes(NEGATIVE_VALUE_TYPES);

// These transactions should use the EOD price / current price to define the net/new contributions
export const HOLDING_CONTRIBUTION_TRANSACTION_TYPES = [
  TransactionTypes.DEPOSIT_BUY,
  TransactionTypes.EXTERNAL_TRANSFER_IN_SECURITY,
  TransactionTypes.TRANSFER_IN_SECURITY,
  TransactionTypes.TRANSFER_OUT_SECURITY,
];
export const GENERATED_HOLDING_CONTRIBUTION_TRANSACTION_TYPES =
  mapToGeneratedTypes(HOLDING_CONTRIBUTION_TRANSACTION_TYPES);

export const ADJUSTED_COST_BASE_TYPES = [
  TransactionTypes.DEPOSIT_BUY,
  TransactionTypes.DISTRIBUTION_REINVESTMENT,
  TransactionTypes.EXTERNAL_TRANSFER_IN_SECURITY,
  TransactionTypes.STOCK_DRIP,
  TransactionTypes.MARKET_DRIP,
  TransactionTypes.TRANSFER_IN_SECURITY,
  TransactionTypes.TRANSFER_OUT_SECURITY,
];
export const GENERATED_ADJUSTED_COST_BASE_TYPES = mapToGeneratedTypes(
  ADJUSTED_COST_BASE_TYPES,
);

export const QUANTITY_PRICE_MULTIPLIERS = [
  TransactionTypes.DEPOSIT_BUY,
  TransactionTypes.EXTERNAL_TRANSFER_IN_SECURITY,
  TransactionTypes.TRANSFER_IN_SECURITY,
  TransactionTypes.TRANSFER_OUT_SECURITY,
  TransactionTypes.STOCK_DRIP,
  TransactionTypes.MARKET_DRIP,
];
export const GENERATED_QUANTITY_PRICE_MULTIPLIERS = mapToGeneratedTypes(
  QUANTITY_PRICE_MULTIPLIERS,
);

export const getValueSign = (
  type: TransactionTypes,
  adjustmentType?: 'DEPOSIT' | 'WITHDRAW',
) => {
  if (POSITIVE_VALUE_TYPES.includes(type)) return 1;
  if (NEGATIVE_VALUE_TYPES.includes(type)) return -1;

  if (adjustmentType) return adjustmentType === 'DEPOSIT' ? 1 : -1;
  return 0;
};

export const isAdjustmentTypeAvailable = (type: TransactionTypes) =>
  !POSITIVE_VALUE_TYPES.includes(type) &&
  !NEGATIVE_VALUE_TYPES.includes(type) &&
  ![
    'SWITCH',
    TransactionTypes.INTEREST,
    TransactionTypes.ACCRUED_INTEREST,
    TransactionTypes.CANCEL_CHEQUE,
    TransactionTypes.CANCEL_INTEREST,
    TransactionTypes.CANCEL_ADJUSTMENT,
    TransactionTypes.CANCEL_TRANSFER,
    TransactionTypes.CANCEL_EFT,
    TransactionTypes.JOURNAL,
  ].includes(type);

export type TransactionInputPros = {
  type: TransactionTypes;
  date?: string;
  subAccountId?: string;
  adjustmentType: 'DEPOSIT' | 'WITHDRAW';
  valueCents?: number;
  value?: number;
  description?: string;
  quantity?: number;
  /**
   * Price in decimal format (e.g., 10.50 for $10.50)
   * Will be converted to cents internally
   */
  price?: number;
  priceCents?: number; // Old code uses priceCents, new code uses price
  financialProductId?: string;
  currency?: Currencies;
  bookCostCents?: number;
  // Journal transaction fields
  journalType?: 'CASH' | 'SECURITY' | 'BOTH';
  toSubAccountId?: string;
  additionalCashCents?: number;
  additionalCash?: number;
  /**
   * Execution order for transactions on the same date.
   * Used to ensure correct ACB/Book Value calculations.
   * For same-fund switches: SWO=0 (sell first), SWI=1 (buy second)
   */
  adjustedCostBaseExecutionOrder?: number;
};

export const calculateValueCents = (item: TransactionInputPros) => {
  const priceCents = dollars2cents(item.price ?? 0, 6);
  const valueCents = dollars2cents(item.value ?? 0);
  if (QUANTITY_PRICE_MULTIPLIERS.includes(item.type)) {
    // Special case for TRANSFER_OUT_SECURITY should always be negative
    if (item.type === TransactionTypes.TRANSFER_OUT_SECURITY) {
      return (item.quantity ?? 0) * priceCents * -1;
    }
    return (item.quantity ?? 0) * priceCents;
  }

  if (item.type === TransactionTypes.CANCEL_TRANSFER_OUT) {
    return valueCents;
  }

  if (item.type === TransactionTypes.CANCEL_TRANSFER_IN) {
    return valueCents * -1;
  }

  return getValueSign(item.type, item.adjustmentType) * valueCents;
};

export const calculateQuantity = (transaction: TransactionInputPros) => {
  if (transaction.type === TransactionTypes.CORPORATE_ACTION) {
    return (
      (transaction.adjustmentType === 'DEPOSIT' ? 1 : -1) *
      (transaction.quantity ?? 0)
    );
  }
  if (
    [
      TransactionTypes.SWO_MARKET,
      TransactionTypes.SWO_COST,
      TransactionTypes.SWI_MARKET,
      TransactionTypes.SWI_COST,
    ].includes(transaction.type)
  ) {
    return getValueSign(transaction.type) * (transaction.quantity ?? 0);
  }
  return (
    getValueSign(transaction.type, transaction.adjustmentType) *
    -1 *
    (transaction.quantity ?? 0)
  );
};

export type Transaction = {
  id: string;
  date?: Date;
  subAccount?: SubAccount;
  account?: Account;
  goal?: Goal;
  user?: User;
  financialProduct?: FinancialProduct;
  type: TransactionTypes;
  quantity?: number;
  priceCents?: number;
  valueCents?: number;
  currency?: Currencies;
  description?: string;
  objectType?: TransactionObjectTypes;
  objectId?: string;
  /**
   * Execution order for transactions on the same date.
   * For same-fund switches: SWO=0 (sell first), SWI=1 (buy second)
   */
  adjustedCostBaseExecutionOrder?: number;
  // Journal transaction fields
  journalType?: 'CASH' | 'SECURITY' | 'BOTH';
  journalPairTransactionId?: string;
  journalFromSubAccount?: SubAccount | string;
  journalToSubAccount?: SubAccount | string;
};
